import { useEffect, useState } from 'react';
import { Download, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { SectionLabel, Reveal } from '../components/Section';

const COLORS = [
  { name: 'KROY Red', hex: '#E8173A', usage: 'Primary accent — buttons, highlights, number emphasis' },
  { name: 'Win Green', hex: '#00C853', usage: 'Positive states, payouts, approved badges' },
  { name: 'Arena Black', hex: '#0D0D0D', usage: 'Page background' },
  { name: 'Slate Card', hex: '#1A1F2E', usage: 'Cards and surfaces' },
  { name: 'Combat White', hex: '#FFFFFF', usage: 'Primary text and titles' },
];

const ASSETS = [
  {
    name: 'Full Logo',
    desc: 'Crown emblem with KROY Kannadiga Royals wordmark — square format, dark background.',
    src: '/kroy-logo.jpeg',
    file: 'kroy-esports-logo.jpeg',
    testid: 'press-download-full-logo',
  },
  {
    name: 'Emblem Icon',
    desc: 'Crown and twin eagles only — transparent PNG, ideal for avatars and app icons.',
    src: '/kroy-emblem.png',
    file: 'kroy-esports-emblem.png',
    testid: 'press-download-emblem',
  },
];

export default function Press() {
  const [settings, setSettings] = useState(null);
  const [copiedHex, setCopiedHex] = useState(null);

  useEffect(() => {
    api.settings().then(setSettings).catch(() => {});
  }, []);

  const copyHex = async (hex) => {
    let ok = false;
    try {
      await navigator.clipboard.writeText(hex);
      ok = true;
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = hex;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand('copy');
        ta.remove();
      } catch {}
    }
    if (ok) {
      setCopiedHex(hex);
      toast.success(`${hex} copied`);
      setTimeout(() => setCopiedHex(null), 1600);
    } else {
      toast.error('Copy failed');
    }
  };

  return (
    <main className="min-h-screen" data-testid="press-page">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <SectionLabel testid="press-label">Press Kit</SectionLabel>
        <Reveal>
          <h1 className="mt-5 font-display text-white text-4xl sm:text-6xl">
            Brand & <span className="text-[#e8173a]">Press Kit</span>
          </h1>
        </Reveal>
        <Reveal delay={0.08}>
          <p className="mt-4 text-[#9e9e9e] text-sm sm:text-base max-w-lg">
            Official KROY Esports assets for partners, sponsors and media. Free to use for coverage and collaborations —
            please don't recolor, stretch or alter the artwork.
          </p>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="press-assets">
          {ASSETS.map((a, i) => (
            <Reveal key={a.name} delay={i * 0.08}>
              <div className="card-surface card-surface-hover overflow-hidden" data-testid={`press-asset-${i}`}>
                <div className="bg-[#0d0d0d] hero-grid flex items-center justify-center p-10 min-h-[220px]">
                  <img src={a.src} alt={a.name} className={i === 0 ? 'w-40 rounded-xl' : 'w-36'} />
                </div>
                <div className="p-5 flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-white font-bold text-sm">{a.name}</h3>
                    <p className="mt-1 text-xs text-[#8a8a8a] leading-relaxed">{a.desc}</p>
                  </div>
                  <a
                    data-testid={a.testid}
                    href={a.src}
                    download={a.file}
                    className="shrink-0 inline-flex items-center gap-2 bg-[#e8173a] hover:bg-[#c0102e] text-white text-[11px] font-extrabold uppercase tracking-wider px-4 py-2.5 rounded-lg transition-all duration-300 active:scale-95"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Download
                  </a>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <h2 className="mt-16 text-sm font-extrabold uppercase tracking-[0.25em] text-[#8a8a8a]" data-testid="press-colors-heading">
            Brand Colors
          </h2>
        </Reveal>
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="press-colors">
          {COLORS.map((c, i) => (
            <Reveal key={c.hex} delay={i * 0.05}>
              <button
                data-testid={`press-color-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => copyHex(c.hex)}
                className="card-surface card-surface-hover overflow-hidden w-full text-left group"
              >
                <div className="h-20 flex items-end justify-end p-2" style={{ background: c.hex }}>
                  <span className="w-7 h-7 rounded-md bg-black/50 backdrop-blur flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    {copiedHex === c.hex ? <Check className="w-3.5 h-3.5 text-[#00c853]" /> : <Copy className="w-3.5 h-3.5" />}
                  </span>
                </div>
                <div className="p-4">
                  <p className="text-white font-bold text-xs">{c.name}</p>
                  <p className="text-[#e8173a] font-mono text-xs mt-0.5">{c.hex}</p>
                  <p className="text-[#8a8a8a] text-[11px] mt-1.5 leading-snug">{c.usage}</p>
                </div>
              </button>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.12}>
          <h2 className="mt-16 text-sm font-extrabold uppercase tracking-[0.25em] text-[#8a8a8a]" data-testid="press-type-heading">
            Typography
          </h2>
        </Reveal>
        <Reveal delay={0.14}>
          <div className="mt-6 card-surface p-7 sm:p-10" data-testid="press-typography">
            <p className="font-display text-white text-3xl sm:text-5xl">Inter — Bold Italic Display</p>
            <p className="mt-3 text-[#9e9e9e] text-base">Inter Regular for body text. Tracked uppercase for labels.</p>
            <p className="mt-4 text-[10px] font-bold uppercase tracking-[0.28em] text-[#8a8a8a]">Section labels look like this</p>
          </div>
        </Reveal>

        <Reveal delay={0.16}>
          <div className="mt-12 card-surface p-7 sm:p-10" data-testid="press-facts">
            <h2 className="text-sm font-extrabold uppercase tracking-[0.25em] text-[#8a8a8a] mb-6">Quick Facts</h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4">
              {[
                ['App', 'KROY Esports (Kannadiga Royals Esports)'],
                ['Tagline', 'Compete. Win. Earn Real Money.'],
                ['Category', 'Competitive mobile esports — BGMI & Free Fire tournaments'],
                ['Platform', 'Android & iOS'],
                ['Website', 'kroyesports.com'],
                settings?.contact_email_1 && ['Press contact', settings.contact_email_1],
              ]
                .filter(Boolean)
                .map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8a8a8a]">{k}</dt>
                    <dd className="text-white font-semibold text-sm mt-1">{v}</dd>
                  </div>
                ))}
            </dl>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
